import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { findAccess, history } from '../../utils';
import { IAuth } from '../../ts/interface/admins.interface';
import { clearSystemData, setNewSystem, systemLoaded } from '../../actionCreators/system';
import { IImage } from '../../ts/interface/image.interface';

import CustomToast from '../../components/Redesing/custom-toast';
import useSmallScreen from '../../hooks/useSmallScreen';
import { Box, Stack, Tab, Tabs, Card as MuiCard, useTheme } from '@mui/material';
import OrganizationTable from './asset/organizations-table';
import SystemTable from './asset/systems-table';

import { useOrganizationStore } from '../../store/organizationStore';
import useOrganization from '../../hooks/use-organization';
import { useState } from 'react';

interface ISystemModalProps {
  system: ISystem;
  auth: IAuth;
  currentSystemId: string;
  isLoaded: boolean;
  loaded: Function;
  clearSystemData: Function;
  updateSystem: Function;
  open: boolean;
  onClose: () => void;
  images: IImage[];
  systemAccess: number;
}

const SystemSelectionModal = ({
  open,
  onClose,
  currentSystemId,
  loaded,
  updateSystem,
  clearSystemData,
  systemAccess,
  auth,
  images,
}: ISystemModalProps) => {
  const isSmallScreen = useSmallScreen();
  const theme = useTheme();
  const organizationInfo = useOrganization();

  const [pageState, setPageState] = useState({
    search: '',
    selectedFilter: [] as { label: string; value: string }[],
    filter: [] as { label: string; value: string }[],
  });

  const [currentTab, setCurrentTab] = React.useState(organizationInfo?.id ? 1 : 0);

  const systemClick = (systemId: string) => {
    if (systemId !== currentSystemId) {
      loaded(false);
      clearSystemData();
      updateSystem(systemId, auth)
        .then(() => {
          loaded(true);
          const setOrganizationInfo = useOrganizationStore.getState().setOrganizationInfo;
          setOrganizationInfo({ id: null, name: null, access: 0 });
          localStorage.removeItem('organizationId');
          // carry the system in the URL so the destination is unambiguous
          // even if a stale organizationId param is still around
          history.push(`/dashboard?systemId=${systemId}`);
          onClose();
        })
        .catch((e: Error) => {
          CustomToast({
            type: 'error',
            message: `there was an error while changing systems. ${e}`,
          });
        });
    }
  };

  const handleSearch = (search: string) => {
    setPageState((prevState) => ({
      ...prevState,
      search,
    }));
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleApplyFilter = (newValues: { label: string; value: string }[]) => {
    const currentFilters = pageState.filter;
    const newFilters = newValues.filter((value) => !currentFilters.includes(value));

    if (newFilters.length > 0) {
      setPageState((prevState) => ({
        ...prevState,
        selectedFilter: newFilters,
        filter: newFilters,
      }));
    }
  };

  const handleDeleteFilter = (filter: { label: string; value: string }[]) => {
    setPageState((prevState) => ({
      ...prevState,
      selectedFilter: prevState.selectedFilter.filter((f) => !filter.includes(f)),
      filter: prevState.filter.filter((f) => !filter.includes(f)),
    }));
  };

  const activeTab = [
    <SystemTable
      currentSystemId={currentSystemId}
      pageState={pageState}
      systemClick={systemClick}
      handleSearch={handleSearch}
      hasAccess={Boolean(
        findAccess(systemAccess)?.includes('partner') || auth.admin.movaticAccess
      )}
      onClose={onClose}
      handleApplyFilter={handleApplyFilter}
      handleDeleteFilter={handleDeleteFilter}
    />,
    <OrganizationTable
      pageState={pageState}
      handleSearch={handleSearch}
      onClose={onClose}
      clearSystemData={() => clearSystemData()}
      hasAccess={true}
    />,
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: isSmallScreen ? '100vw' : '80vw',
          margin: isSmallScreen ? 1 : 'auto',
          width: '100%',
        },
      }}
    >
      <DialogContent>
        <Box sx={{ width: '100%', height: '85vh' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleChange}
              textColor={theme.palette.text.primary as any}
              indicatorColor="secondary"
              scrollButtons="auto"
              variant="scrollable"
              id="sys-org-tabs"
            >
              <Tab label="Systems" />
              <Tab label="Organizations" />
            </Tabs>
          </Box>
          <Stack paddingTop={1}>
            <MuiCard>{activeTab[currentTab]}</MuiCard>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default connect(
  (state: {
    system: { current: ISystem; isLoaded: boolean; systems: ISystem[]; images: IImage[] };
    admin: { admin: IAuth; systemAccess: number };
  }) => ({
    system: state.system.current,
    systems: state.system.systems,
    isLoaded: state.system.isLoaded,
    currentSystemId: state.system.current.id,
    auth: state.admin.admin,
    images: state.system.images,
    systemAccess: state.admin.systemAccess,
  }),
  (dispatch: Function) => ({
    clearSystemData() {
      dispatch(clearSystemData());
    },
    updateSystem(systemId: string, auth: IAuth) {
      return dispatch(setNewSystem(systemId, auth));
    },
    loaded(bool: boolean) {
      dispatch(systemLoaded(bool));
    },
  })
)(SystemSelectionModal);
